import { useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { discoverPosts } from "@/mocks/discoverData";
import type { RelatedProduct } from "@/mocks/discoverData";

const BRAND = "#FF6B35";

const typeColorMap: Record<string, string> = {
  hotel: "#D97706",
  route: "#059669",
  car: "#2563EB",
  guide: "#7C3AED",
};
const typeLabelMap: Record<string, string> = {
  hotel: "酒店",
  route: "线路",
  car: "租车",
  guide: "导游",
};
const typeIconMap: Record<string, string> = {
  hotel: "ri-hotel-bed-line",
  route: "ri-route-line",
  car: "ri-car-line",
  guide: "ri-user-star-line",
};

// ── 关联产品卡片 ──
function ProductCard({ product }: { product: RelatedProduct }) {
  const navigate = useNavigate();
  const color = typeColorMap[product.type] ?? BRAND;

  return (
    <div
      className="flex-shrink-0 rounded-2xl overflow-hidden cursor-pointer active:scale-[0.98] transition-all"
      style={{ width: 200, border: "1.5px solid #F0F0F0", background: "#fff" }}
      onClick={() => navigate(product.path)}
    >
      <div className="relative overflow-hidden" style={{ height: 110 }}>
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover object-top"
        />
        <div
          className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-full"
          style={{ background: `${color}E0`, backdropFilter: "blur(4px)" }}
        >
          <i className={typeIconMap[product.type]} style={{ color: "#fff", fontSize: 10 }} />
          <span className="text-xs font-semibold text-white">{typeLabelMap[product.type]}</span>
        </div>
      </div>
      <div className="p-2.5">
        <div className="text-xs font-bold text-gray-800 line-clamp-1 mb-0.5">{product.name}</div>
        <div className="text-xs text-gray-400 line-clamp-1 mb-2">{product.desc}</div>
        <div className="flex flex-wrap gap-1 mb-2">
          {product.tags.slice(0, 2).map(tag => (
            <span
              key={tag}
              className="text-xs px-1.5 py-0.5 rounded-full"
              style={{ background: `${color}12`, color }}
            >
              {tag}
            </span>
          ))}
        </div>
        <div className="flex items-end justify-between">
          <div>
            <span className="text-sm font-bold" style={{ color: BRAND }}>{product.price}</span>
            <span className="text-xs text-gray-400 ml-0.5">{product.priceUnit}</span>
          </div>
          <div className="flex items-center gap-0.5">
            <i className="ri-star-fill" style={{ color: "#F59E0B", fontSize: 11 }} />
            <span className="text-xs font-semibold text-gray-600">{product.rating}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── 图片查看器 ──
function ImageViewer({
  images,
  initialIdx,
  onClose,
}: {
  images: string[];
  initialIdx: number;
  onClose: () => void;
}) {
  const [idx, setIdx] = useState(initialIdx);
  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      style={{ background: "rgba(0,0,0,0.95)" }}
      onClick={onClose}
    >
      <div className="flex justify-end p-4">
        <button
          className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer"
          style={{ background: "rgba(255,255,255,0.15)" }}
          onClick={onClose}
        >
          <i className="ri-close-line text-white" style={{ fontSize: 20 }} />
        </button>
      </div>
      <div
        className="flex-1 flex items-center justify-center"
        onClick={e => e.stopPropagation()}
      >
        <img
          src={images[idx]}
          alt=""
          className="max-w-full max-h-full object-contain"
          style={{ maxHeight: "70vh" }}
        />
      </div>
      <div className="flex justify-center gap-2 py-4">
        {images.map((_, i) => (
          <button
            key={i}
            onClick={e => { e.stopPropagation(); setIdx(i); }}
            className="rounded-full transition-all cursor-pointer"
            style={{
              width: i === idx ? 20 : 7,
              height: 7,
              background: i === idx ? BRAND : "rgba(255,255,255,0.35)",
            }}
          />
        ))}
      </div>
      <div className="flex justify-between px-6 pb-8">
        <button
          className="w-10 h-10 flex items-center justify-center rounded-full cursor-pointer"
          style={{ background: idx > 0 ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.06)" }}
          onClick={e => { e.stopPropagation(); if (idx > 0) setIdx(idx - 1); }}
        >
          <i className="ri-arrow-left-s-line text-white" style={{ fontSize: 22 }} />
        </button>
        <span className="text-white/60 text-sm self-center">{idx + 1} / {images.length}</span>
        <button
          className="w-10 h-10 flex items-center justify-center rounded-full cursor-pointer"
          style={{ background: idx < images.length - 1 ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.06)" }}
          onClick={e => { e.stopPropagation(); if (idx < images.length - 1) setIdx(idx + 1); }}
        >
          <i className="ri-arrow-right-s-line text-white" style={{ fontSize: 22 }} />
        </button>
      </div>
    </div>
  );
}

export default function PostDetailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const postId = Number(searchParams.get("id") ?? "1");
  const post = discoverPosts.find(p => p.id === postId) ?? discoverPosts[0];

  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(post.likes);
  const [collected, setCollected] = useState(false);
  const [viewerImg, setViewerImg] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const commentRef = useRef<HTMLInputElement>(null);

  const paragraphs = post.content.split("\n\n").filter(Boolean);

  return (
    <div
      className="min-h-screen pb-[calc(env(safe-area-inset-bottom,0px)+96px)]"
      style={{ background: "#F7F7F7", fontFamily: "'SF Pro Text','PingFang SC','Inter',system-ui,sans-serif" }}
    >
      {/* ── 顶部导航 ── */}
      <div
        className="sticky top-0 z-40 bg-white flex items-center justify-between px-4 pb-3"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 48px)", borderBottom: "1px solid #F0F0F0" }}
      >
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 flex items-center justify-center rounded-xl cursor-pointer"
          style={{ background: "#F5F5F5" }}
        >
          <i className="ri-arrow-left-line text-gray-700" style={{ fontSize: 18 }} />
        </button>
        <span className="text-sm font-bold text-gray-900">旅行日记</span>
        <button
          className="w-9 h-9 flex items-center justify-center rounded-xl cursor-pointer"
          style={{ background: "#F5F5F5" }}
        >
          <i className="ri-share-forward-line text-gray-600" style={{ fontSize: 17 }} />
        </button>
      </div>

      {/* ── 作者信息 ── */}
      <div className="bg-white px-4 pt-4 pb-3">
        <div className="flex items-center gap-3">
          {/* 头像 + 姓名叠加 */}
          <div className="relative flex-shrink-0">
            <img
              src={post.avatar}
              alt={post.username}
              className="w-12 h-12 rounded-full object-cover object-top"
              style={{ border: `2px solid ${BRAND}` }}
            />
            {/* 姓名在头像上方 */}
            <div
              className="absolute -top-5 left-1/2 -translate-x-1/2 whitespace-nowrap px-2 py-0.5 rounded-full text-white text-xs font-bold"
              style={{ background: BRAND, fontSize: 10 }}
            >
              {post.username}
            </div>
          </div>
          <div className="flex-1 min-w-0 ml-1">
            <div className="text-sm font-bold text-gray-900">{post.username}</div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <i className="ri-map-pin-line" style={{ color: BRAND, fontSize: 11 }} />
                {post.location}
              </span>
              <span className="text-xs text-gray-300">·</span>
              <span className="text-xs text-gray-400">{post.publishTime}</span>
            </div>
          </div>
          <button
            className="px-4 py-1.5 rounded-full text-xs font-bold cursor-pointer whitespace-nowrap"
            style={{ background: "#FFF3EE", color: BRAND }}
          >
            + 关注
          </button>
        </div>
      </div>

      {/* ── 主图 ── */}
      <div className="px-4 pt-3">
        <div
          className="rounded-2xl overflow-hidden cursor-pointer relative"
          style={{ height: 260 }}
          onClick={() => setViewerImg(0)}
        >
          <img
            src={post.images[0]}
            alt={post.title}
            className="w-full h-full object-cover object-top"
          />
          {post.images.length > 1 && (
            <div
              className="absolute bottom-3 right-3 px-2.5 py-1 rounded-full text-white text-xs font-semibold flex items-center gap-1"
              style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
            >
              <i className="ri-image-2-line" style={{ fontSize: 12 }} />
              {post.images.length}
            </div>
          )}
        </div>
      </div>

      {/* ── 标题 ── */}
      <div className="px-4 pt-4 pb-2 bg-white mt-3">
        <h1 className="text-lg font-bold text-gray-900 leading-tight mb-2">{post.title}</h1>
        <div className="flex flex-wrap gap-1.5">
          {post.tags.map(tag => (
            <span
              key={tag}
              className="text-xs px-2.5 py-0.5 rounded-full font-medium"
              style={{ background: "#FFF3EE", color: BRAND }}
            >
              #{tag}
            </span>
          ))}
        </div>
      </div>

      {/* ── 图片网格 ── */}
      {post.images.length > 1 && (
        <div className="px-4 pt-3">
          <div className="grid grid-cols-3 gap-1.5">
            {post.images.slice(1).map((img, i) => (
              <div
                key={i}
                className="rounded-xl overflow-hidden cursor-pointer"
                style={{ height: 90 }}
                onClick={() => setViewerImg(i + 1)}
              >
                <img src={img} alt="" className="w-full h-full object-cover object-top" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── 正文内容 ── */}
      <div className="mx-4 mt-3 bg-white rounded-2xl p-4">
        <div className="space-y-3">
          {paragraphs.map((para, i) => (
            <p key={i} className="text-sm text-gray-700 leading-relaxed">
              {para}
            </p>
          ))}
        </div>
      </div>

      {/* ── 关联产品 / 旅游套餐 ── */}
      {post.relatedProducts.length > 0 && (
        <div className="mt-3">
          <div className="px-4 flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-1 h-4 rounded-full" style={{ background: BRAND }} />
              <span className="text-sm font-bold text-gray-900">帖子关联产品</span>
              <span className="text-xs text-gray-400">{post.relatedProducts.length} 个推荐</span>
            </div>
            {/* AI快速询单按钮 */}
            <button
              onClick={() => navigate("/ai-plan", {
                state: {
                  fromPost: true,
                  postTitle: post.title,
                  postTags: post.tags,
                  relatedProducts: post.relatedProducts.map(p => ({
                    name: p.name,
                    type: p.type,
                    price: p.price,
                  })),
                }
              })}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full cursor-pointer active:scale-95 transition-all whitespace-nowrap"
              style={{ background: "linear-gradient(135deg, #FF6B35, #FF9A5C)", boxShadow: "0 2px 10px rgba(255,107,53,0.3)" }}
            >
              <i className="ri-sparkling-2-line text-white" style={{ fontSize: 13 }} />
              <span className="text-xs font-bold text-white">AI快速询单</span>
            </button>
          </div>
          <div className="pl-4 pr-0 overflow-x-auto">
            <div className="flex gap-3 pb-2" style={{ width: "max-content", paddingRight: 16 }}>
              {post.relatedProducts.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
          {/* AI询单引导条 */}
          <div
            className="mx-4 mt-2 px-4 py-3 rounded-2xl flex items-center gap-3 cursor-pointer active:scale-[0.99] transition-all"
            style={{ background: "linear-gradient(135deg, #FFF3EE, #FFF8F5)", border: "1px solid #FFD4C2" }}
            onClick={() => navigate("/ai-plan", {
              state: {
                fromPost: true,
                postTitle: post.title,
                postTags: post.tags,
                relatedProducts: post.relatedProducts.map(p => ({
                  name: p.name,
                  type: p.type,
                  price: p.price,
                })),
              }
            })}
          >
            <div
              className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0"
              style={{ background: "linear-gradient(135deg, #FF6B35, #FF9A5C)" }}
            >
              <i className="ri-sparkling-2-line text-white" style={{ fontSize: 18 }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold" style={{ color: "#FF6B35" }}>AI快速询单</div>
              <div className="text-xs text-gray-400 mt-0.5">
                根据「{post.title}」关联产品，为您智能生成旅行计划
              </div>
            </div>
            <i className="ri-arrow-right-s-line flex-shrink-0" style={{ color: "#FF6B35", fontSize: 18 }} />
          </div>
        </div>
      )}

      {/* ── 互动数据 ── */}
      <div className="mx-4 mt-3 bg-white rounded-2xl px-4 py-3 flex items-center gap-6">
        <button
          className="flex items-center gap-1.5 cursor-pointer"
          onClick={() => {
            setLiked(!liked);
            setLikeCount(liked ? likeCount - 1 : likeCount + 1);
          }}
        >
          <i
            className={liked ? "ri-heart-fill" : "ri-heart-line"}
            style={{ color: liked ? "#EF4444" : "#9CA3AF", fontSize: 20 }}
          />
          <span className="text-sm font-semibold" style={{ color: liked ? "#EF4444" : "#6B7280" }}>
            {likeCount >= 1000 ? (likeCount / 1000).toFixed(1) + "k" : likeCount}
          </span>
        </button>
        <button
          className="flex items-center gap-1.5 cursor-pointer"
          onClick={() => commentRef.current?.focus()}
        >
          <i className="ri-chat-1-line" style={{ color: "#9CA3AF", fontSize: 20 }} />
          <span className="text-sm font-semibold text-gray-500">{post.comments}</span>
        </button>
        <button
          className="flex items-center gap-1.5 cursor-pointer ml-auto"
          onClick={() => setCollected(!collected)}
        >
          <i
            className={collected ? "ri-bookmark-fill" : "ri-bookmark-line"}
            style={{ color: collected ? BRAND : "#9CA3AF", fontSize: 20 }}
          />
          <span className="text-sm font-semibold" style={{ color: collected ? BRAND : "#6B7280" }}>
            {collected ? "已收藏" : "收藏"}
          </span>
        </button>
      </div>

      {/* ── 评论区 ── */}
      <div className="mx-4 mt-3 bg-white rounded-2xl p-4 mb-4">
        <div className="text-sm font-bold text-gray-900 mb-4">
          评论 <span className="text-gray-400 font-normal">{post.comments}</span>
        </div>
        {/* mock评论 */}
        {[
          { name: "背包旅行者Tim", time: "2天前", text: "太详细了！正好要去三亚，收藏备用！", avatar: "https://readdy.ai/api/search-image?query=young%20man%20avatar%20portrait%20casual%20style%20clean%20background%20friendly&width=32&height=32&seq=c001&orientation=squarish", likes: 23 },
          { name: "云游四海的鱼", time: "3天前", text: "蜈支洲岛确实美，就是人多了点，早去人少👍", avatar: "https://readdy.ai/api/search-image?query=woman%20avatar%20portrait%20smiling%20natural%20background%20casual%20outdoor&width=32&height=32&seq=c002&orientation=squarish", likes: 18 },
          { name: "行走的相机",  time: "5天前", text: "亚特兰蒂斯水世界我也带孩子去了，玩到不肯走！", avatar: "https://readdy.ai/api/search-image?query=chinese%20man%20portrait%20outdoor%20travel%20style%20casual%20friendly&width=32&height=32&seq=c003&orientation=squarish", likes: 11 },
        ].map((c, i) => (
          <div
            key={i}
            className="flex gap-3 py-3"
            style={{ borderTop: i > 0 ? "1px solid #F5F5F5" : "none" }}
          >
            <div className="relative flex-shrink-0">
              <img
                src={c.avatar}
                alt={c.name}
                className="w-9 h-9 rounded-full object-cover object-top"
              />
              {/* 评论者姓名在头像上方 */}
              <div
                className="absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap px-1.5 py-0.5 rounded-full text-white"
                style={{ background: "rgba(0,0,0,0.55)", fontSize: 9 }}
              >
                {c.name}
              </div>
            </div>
            <div className="flex-1 min-w-0 pt-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-gray-700">{c.name}</span>
                <span className="text-xs text-gray-300">{c.time}</span>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{c.text}</p>
              <button className="flex items-center gap-1 mt-2 cursor-pointer">
                <i className="ri-heart-line" style={{ color: "#C4C8D0", fontSize: 14 }} />
                <span className="text-xs text-gray-300">{c.likes}</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* ── 底部评论输入 ── */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-white px-4 z-40 flex items-center gap-3"
        style={{ borderTop: "1px solid #F0F0F0", paddingTop: "8px", paddingBottom: "calc(8px + env(safe-area-inset-bottom, 0px))" }}
      >
        <div className="flex-1 flex items-center gap-2 rounded-full px-4 py-2.5 bg-gray-100">
          <input
            ref={commentRef}
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder="说说你的想法..."
            className="flex-1 bg-transparent outline-none text-sm text-gray-700"
          />
        </div>
        <button
          className="w-10 h-10 flex items-center justify-center rounded-full cursor-pointer flex-shrink-0"
          style={{ background: comment.trim() ? BRAND : "#F5F5F5" }}
        >
          <i
            className="ri-send-plane-fill"
            style={{ color: comment.trim() ? "#fff" : "#C4C8D0", fontSize: 17 }}
          />
        </button>
      </div>

      {/* ── 图片查看器 ── */}
      {viewerImg !== null && (
        <ImageViewer
          images={post.images}
          initialIdx={viewerImg}
          onClose={() => setViewerImg(null)}
        />
      )}
    </div>
  );
}
